/**
 * TanStack Query hooks for the ticket feature.
 *
 * Query keys are hierarchical so a mutation can invalidate exactly what it
 * makes stale: `["tickets"]` is the root, `["tickets", "list", …]` the list,
 * `["tickets", "detail", id]` one ticket, `["tickets", "timeline", id, …]` its
 * activity. Every write invalidates the detail and timeline of the ticket it
 * touched plus every list, because a status change reorders and re-buckets the
 * list the user came from.
 *
 * They live in the feature rather than `src/lib/api/hooks.ts` because they are
 * only used here; promote them to the shared hooks module if a second feature
 * (the assistant raising a ticket from a conversation, say) needs them.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useLanguage } from "@/i18n";
import { logger } from "@/lib/logger";

import type {
  IncidentFollowupsRequest,
  ListRequestOfferingsQuery,
  ListTicketsQuery,
  ListTicketTimelineQuery,
  SimilarTicketJoinRequest,
  SimilarTicketsRequest,
  TicketCloseRequest,
  TicketCommentCreateRequest,
  TicketCreateRequest,
  TicketDetail,
  TicketInfoAnswerRequest,
  TicketReopenRequest,
  TicketResolutionConfirmRequest,
} from "./contract";
import {
  addTicketComment,
  answerTicketInfoRequest,
  closeTicket,
  confirmTicketResolution,
  createTicket,
  findSimilarTickets,
  getIncidentFollowups,
  getRequestOffering,
  getTicket,
  getTicketAttachmentDownload,
  getTicketByNumber,
  getTicketMetadata,
  joinSimilarTicket,
  listNamedOnTickets,
  listRequestOfferings,
  listRoleLists,
  listTickets,
  listTicketTimeline,
  newIdempotencyKey,
  reopenTicket,
} from "./operations";

/** Every key this feature caches under, so invalidation stays greppable. */
export const ticketKeys = {
  all: ["tickets"] as const,
  metadata: () => [...ticketKeys.all, "metadata"] as const,
  lists: () => [...ticketKeys.all, "list"] as const,
  list: (query: ListTicketsQuery) => [...ticketKeys.lists(), query] as const,
  // Under `lists()`, so every write that refreshes the lists refreshes this too.
  namedOn: (translateTo: string | undefined) =>
    [...ticketKeys.lists(), "named-on", translateTo ?? null] as const,
  details: () => [...ticketKeys.all, "detail"] as const,
  byNumber: (ticketNumber: string, translateTo: string | undefined) =>
    [...ticketKeys.all, "by-number", ticketNumber, translateTo ?? null] as const,
  detail: (ticketId: string, translateTo: string | undefined) =>
    [...ticketKeys.details(), ticketId, translateTo ?? null] as const,
  timelines: () => [...ticketKeys.all, "timeline"] as const,
  timeline: (ticketId: string, query: ListTicketTimelineQuery) =>
    [...ticketKeys.timelines(), ticketId, query] as const,
  attachment: (ticketId: string, attachmentId: string) =>
    [...ticketKeys.all, "attachment", ticketId, attachmentId] as const,
  offerings: () => [...ticketKeys.all, "offerings"] as const,
  offeringList: (query: ListRequestOfferingsQuery) => [...ticketKeys.offerings(), query] as const,
  offering: (offeringId: string, translateTo: string | undefined) =>
    [...ticketKeys.offerings(), offeringId, translateTo ?? null] as const,
  roleLists: () => [...ticketKeys.all, "role-lists"] as const,
};

/**
 * The language to ask the server to translate provider-authored text into.
 *
 * Contract §2: `translate_to` is the *only* thing that translates stored ticket
 * content, it is opt-in, and the response always carries the original alongside.
 * Sending the active UI language means a Danish employee reads an English
 * journal in Danish, with "translated — show original" always one click away.
 */
export function useTranslateTo(): string {
  const { language } = useLanguage();
  // The language tag ("da"), not the formatting locale ("da-DK"): the server
  // matches `translate_to` against `listSupportedLanguages`, and a region
  // subtag it does not publish would be an unnecessary near-miss.
  return language;
}

// ─── Reads ───────────────────────────────────────────────────────────────

export function useTicketList(query: ListTicketsQuery) {
  return useQuery({
    queryKey: ticketKeys.list(query),
    queryFn: () => listTickets(query),
    // A ticket list that lags a few seconds is fine; hammering Ivanti is not.
    staleTime: 30_000,
    // Keeps the previous page on screen while the next one loads, so the grid
    // does not collapse to a skeleton on every filter change.
    placeholderData: (previous) => previous,
  });
}

/**
 * Requests that name the signed-in user ("Named on", 2 Oct). Mounted only
 * inside that tab's panel, so it is read when the tab is opened: it costs
 * Ivanti several reads, and most people are named on nothing.
 */
export function useNamedOnTickets(translateTo: string | undefined) {
  return useQuery({
    queryKey: ticketKeys.namedOn(translateTo),
    queryFn: () => listNamedOnTickets(translateTo),
    staleTime: 30_000,
  });
}

export function useTicket(ticketId: string | undefined, translateTo?: string) {
  return useQuery({
    queryKey: ticketKeys.detail(ticketId ?? "", translateTo),
    queryFn: () => getTicket(ticketId as string, translateTo),
    enabled: ticketId !== undefined && ticketId.length > 0,
    staleTime: 15_000,
  });
}

/**
 * Resolves a human ticket number (from an Ivanti notification email) to the ticket.
 *
 * The detail it returns is seeded into the by-id cache, so the page the deep
 * link redirects to renders at once instead of reading the same ticket twice.
 * No retry: a 404 or 403 is an answer, not a blip.
 */
export function useTicketByNumber(ticketNumber: string, translateTo?: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ticketKeys.byNumber(ticketNumber, translateTo),
    queryFn: async () => {
      const detail = await getTicketByNumber(ticketNumber, translateTo);
      logger.debug("[tickets] resolved number", ticketNumber, "→", detail.ticket_id);
      queryClient.setQueryData(ticketKeys.detail(detail.ticket_id, translateTo), detail);
      return detail;
    },
    enabled: ticketNumber.length > 0,
    retry: false,
    staleTime: 15_000,
  });
}

export function useTicketTimeline(
  ticketId: string | undefined,
  query: ListTicketTimelineQuery = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ticketKeys.timeline(ticketId ?? "", query),
    queryFn: () => listTicketTimeline(ticketId as string, query),
    enabled: enabled && ticketId !== undefined && ticketId.length > 0,
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
}

/** Tenant pick lists. Cached hard — the server caches them too. */
export function useTicketMetadata() {
  return useQuery({
    queryKey: ticketKeys.metadata(),
    queryFn: getTicketMetadata,
    staleTime: 10 * 60_000,
  });
}

export function useRequestOfferings(query: ListRequestOfferingsQuery, enabled = true) {
  return useQuery({
    queryKey: ticketKeys.offeringList(query),
    queryFn: () => listRequestOfferings(query),
    enabled,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useRequestOffering(offeringId: string | null, translateTo?: string) {
  return useQuery({
    queryKey: ticketKeys.offering(offeringId ?? "", translateTo),
    queryFn: () => getRequestOffering(offeringId as string, translateTo),
    enabled: offeringId !== null && offeringId.length > 0,
    staleTime: 5 * 60_000,
  });
}

/**
 * The Master approval matrix's role pickers (8 Oct): a shipped file, the same
 * for everyone, so read once. Only fetched by a form showing the Bluestar
 * area picker.
 */
export function useRoleLists(enabled = true) {
  return useQuery({
    queryKey: ticketKeys.roleLists(),
    queryFn: listRoleLists,
    enabled,
    staleTime: Infinity,
  });
}

/**
 * Resolves a single-use download descriptor for an attachment.
 *
 * Deliberately a mutation, not a query: the contract says the `url_path` is
 * single-use and short-lived, so caching one would hand the user a dead link.
 */
export function useAttachmentDownload() {
  return useMutation({
    mutationFn: ({ ticketId, attachmentId }: { ticketId: string; attachmentId: string }) =>
      getTicketAttachmentDownload(ticketId, attachmentId),
  });
}

// ─── Writes ──────────────────────────────────────────────────────────────

/** Invalidates everything a write to one ticket makes stale. */
function useTicketInvalidator() {
  const queryClient = useQueryClient();
  return (ticketId: string) => {
    void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: [...ticketKeys.details(), ticketId] });
    void queryClient.invalidateQueries({ queryKey: [...ticketKeys.timelines(), ticketId] });
  };
}

/**
 * Writes the fresh detail the server returned straight into the cache.
 *
 * Every write endpoint answers with the full `TicketDetail`, so seeding it
 * means the page re-renders from the authoritative object immediately and the
 * invalidation that follows is a background refresh rather than a spinner.
 */
function useDetailSeeder() {
  const queryClient = useQueryClient();
  return (detail: TicketDetail) => {
    queryClient.setQueriesData<TicketDetail>(
      { queryKey: [...ticketKeys.details(), detail.ticket_id] },
      detail,
    );
  };
}

export interface CreateTicketVariables {
  body: TicketCreateRequest;
  /**
   * Stable across retries of the same submit attempt. Generated per attempt by
   * the form; the contract requires the header and dedupes on it for 24h.
   */
  idempotencyKey?: string;
  /** Stop waiting for the response, e.g. `AbortSignal.timeout(…)`. */
  signal?: AbortSignal;
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  const seedDetail = useDetailSeeder();

  return useMutation({
    mutationFn: ({ body, idempotencyKey, signal }: CreateTicketVariables) =>
      createTicket(body, idempotencyKey ?? newIdempotencyKey(), signal),
    onSuccess: (detail) => {
      logger.debug("[tickets] created", detail.ticket_number);
      seedDetail(detail);
      void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
    },
  });
}

/**
 * The incident form's "Continue" step: ask the server which follow-up
 * questions (if any) are worth asking. A mutation, not a query — it is an
 * action taken once per press, and a cached answer for a description the
 * person has since edited would be the wrong one.
 */
export function useIncidentFollowups() {
  return useMutation({
    mutationFn: (body: IncidentFollowupsRequest) => getIncidentFollowups(body),
    retry: false,
  });
}

/** "Is this the same problem?": the check run just before an incident is filed. */
export function useFindSimilarTickets() {
  return useMutation({
    mutationFn: (body: SimilarTicketsRequest) => findSimilarTickets(body),
    retry: false,
  });
}

/** "Yes, add me to it". */
export function useJoinSimilarTicket() {
  return useMutation({
    mutationFn: (body: SimilarTicketJoinRequest) => joinSimilarTicket(body),
    retry: false,
  });
}

export function useAddTicketComment(ticketId: string) {
  const invalidate = useTicketInvalidator();
  return useMutation({
    mutationFn: (body: TicketCommentCreateRequest) => addTicketComment(ticketId, body),
    onSuccess: () => invalidate(ticketId),
  });
}

export interface AnswerInfoRequestVariables {
  infoRequestId: string;
  body: TicketInfoAnswerRequest;
}

export function useAnswerInfoRequest(ticketId: string) {
  const invalidate = useTicketInvalidator();
  const seedDetail = useDetailSeeder();
  return useMutation({
    mutationFn: ({ infoRequestId, body }: AnswerInfoRequestVariables) =>
      answerTicketInfoRequest(ticketId, infoRequestId, body),
    onSuccess: (detail) => {
      seedDetail(detail);
      invalidate(ticketId);
    },
  });
}

export function useCloseTicket(ticketId: string) {
  const invalidate = useTicketInvalidator();
  const seedDetail = useDetailSeeder();
  return useMutation({
    mutationFn: (body: TicketCloseRequest) => closeTicket(ticketId, body),
    onSuccess: (detail) => {
      seedDetail(detail);
      invalidate(ticketId);
    },
  });
}

export function useConfirmResolution(ticketId: string) {
  const invalidate = useTicketInvalidator();
  const seedDetail = useDetailSeeder();
  return useMutation({
    mutationFn: (body: TicketResolutionConfirmRequest) => confirmTicketResolution(ticketId, body),
    onSuccess: (detail) => {
      seedDetail(detail);
      invalidate(ticketId);
    },
  });
}

export function useReopenTicket(ticketId: string) {
  const invalidate = useTicketInvalidator();
  const seedDetail = useDetailSeeder();
  return useMutation({
    mutationFn: (body: TicketReopenRequest) => reopenTicket(ticketId, body),
    onSuccess: (detail) => {
      seedDetail(detail);
      invalidate(ticketId);
    },
  });
}
